import React, { useEffect } from "react";
import { EqualHeightElement, EqualHeightContext } from "react-equal-height/clean";
import Collapse from "@mui/material/Collapse";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import DtvAvatar from "@directv-digital/dds.ui.dtv-avatar";
import { PackageFeatureList } from "./package-feature-list";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvTheme, { TABLET_WIDTH } from "@directv-digital/dds.ui.dtv-theme";
import Checkbox from "@directv-digital/fusion.atoms.checkbox";
// Generated by Copilot- Start - 2026-04-10 16:47:32
// Import the optional variant so components outside a provider get undefined
// instead of a thrown error — no try/catch wrapper needed (hooks-rule compliant).
import { useOptionalComparePkgTrayContext } from "./../../ComparePackageTray";
// Generated by Copilot- End - 2026-04-10 16:47:32
import { fireModalOpenCloseEventGA, getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import { modalLinkDestination, usePageUrl } from "src/utils/link-click";

export interface PackageFeatureProps {
  features?: any;
  lazyLoadImages?: boolean;
  textColor?: any;
  whatYouGet?: any;
  noTopBorder?: boolean;
  packageCardVariant?: string;
  comparePackagesCta?: any;
  disableEqualHeight?: boolean;
  openComparePackagesModal: (cardPosition) => void;
  open?: boolean;
  id?: any;
  showNoLocalChannels?: boolean;
  queryparam?: string;
}

// Generated by Copilot- Start - 2026-04-10 16:47:32
const COMPARE_PKG_TRAY_FALLBACK = {
  compareText: "",
  isComparePkgTray: false,
  selectedItems: [] as any[],
  updateSelectedTag: (_tag: any) => undefined,
  setTrayOpen: (_open: boolean) => undefined,
  trayOpen: false,
  updateCollapsedTray: (_collapsed: boolean) => undefined
};
// Generated by Copilot- End - 2026-04-10 16:47:32

export const PackageFeature = (props: PackageFeatureProps) => {
  // Generated by Copilot- Start - 2026-04-10 16:47:32
  // useOptionalComparePkgTrayContext calls useContext directly — no throw, no try/catch.
  // This is hooks-rule compliant: hooks are called unconditionally at the top level.
  const comparePkgTrayCtx = useOptionalComparePkgTrayContext() ?? COMPARE_PKG_TRAY_FALLBACK;
  const { compareText, isComparePkgTray, selectedItems, updateSelectedTag, setTrayOpen, trayOpen, updateCollapsedTray } = comparePkgTrayCtx;
  // WAT-12478 — GTM auto-capture reports the compare checkbox from its input's attributes.
  const pageUrl = usePageUrl();
  // Generated by Copilot- End - 2026-04-10 16:47:32
  try {
    const {
      features,
      // lazyLoadImages,
      textColor,
      whatYouGet,
      packageCardVariant,
      comparePackagesCta,
      disableEqualHeight,
      openComparePackagesModal,
      open: _open,
      id,
      noTopBorder,
      showNoLocalChannels,
      queryparam
    } = props;
    const { setForceUpdate: forceUpdate   } = React.useContext(EqualHeightContext);
    // This is used to expand all the what you get sections when the user clicks on the arrow icon
    const [expanded, setExpanded] = React.useState(false);
    const handleClick = () => {
      window.dispatchEvent(
        new CustomEvent('expandAllPackages', { detail: !expanded })
      )
    }
    const handleCompareEvent = queryparam => {
      updateSelectedTag(queryparam);
      const isCollapsed = !selectedItems.includes(queryparam);
      updateCollapsedTray(isCollapsed);
      setTrayOpen(true);
      !trayOpen && fireModalOpenCloseEventGA({
        dim_name: "open",
        dim_value: "comparison_tray"
      });
    }
    useEffect(() => {
      const handleExpandAll = e => {
        setExpanded(e.detail)
      }
      window.addEventListener('expandAllPackages', handleExpandAll);
      return () => window.removeEventListener('expandAllPackages', handleExpandAll);
    }, []);

    // Track only the tablet-and-up boolean (not the raw width) so React bails
    // out of re-rendering every card on each resize event that doesn't cross
    // the breakpoint. window.parent is kept for iframe (preview) contexts.
    const [isTabletUp, setIsTabletUp] = React.useState(true)
    React.useEffect(() => {
      function handleResize() {
        if(typeof window !== "undefined"){
          setIsTabletUp((window?.parent?.innerWidth || window?.innerWidth) >= TABLET_WIDTH)
        }
      }
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [])

    useEffect(() => {
      forceUpdate && forceUpdate(value => !value);
    }, [expanded]);

    useEffect(() => {
      if (disableEqualHeight) {
        forceUpdate && forceUpdate(value => !value);
      }
    }, [disableEqualHeight]);

    useEffect(() => {
      // Select the node that will be observed for mutations
      if (typeof document !== "undefined") {
        const targetNode = document.getElementById(`${id}-pkg-feature-list`);
        const config = { childList: true };

        const callback = mutationList => {
          for (const mutation of mutationList) {
            if (mutation.type === "childList") {
              forceUpdate && forceUpdate(val => !val);
            }
          }
        };

        // Create an observer instance linked to the callback function
        const observer = new MutationObserver(callback);

        // Start observing the target node for configured mutations
        if (targetNode && expanded && !disableEqualHeight) {
          observer.observe(targetNode, config);
        } else {
          observer.disconnect();
        }

        return () => {
          observer.disconnect();
        };
      }
    }, [expanded, disableEqualHeight]);

    return (
      <>
        <EqualHeightElement
          disable={disableEqualHeight}
          name="plan-card-what-you-get-cta"
        >
          <DtvBox
            id={`${id}-whatYouGet-cta-container`}
            sx={{
              ...(isComparePkgTray && { margin: "20px 0 0 0" }),
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            {whatYouGet?.cta?.label && (
              <DtvButton
                aria-expanded={expanded}
                id={`${id}-whatYouGetCta`}
                variant="uxtext"
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "none",
                    backgroundColor: noTopBorder ? "transparent" : "auto"
                  },
                  "&:active": {
                    textDecoration: noTopBorder ? "none" : "underline",
                    backgroundColor: noTopBorder ? "transparent" : "auto"
                  },
                  [DtvTheme.breakpoints.between(DtvTheme.breakpoints.values.sm, DtvTheme.breakpoints.values.md)]: {
                    ...(isComparePkgTray && {
                      fontSize: "14px !important"
                    })
                  },
                  ...(!isComparePkgTray && { margin: "20px 0 0 0" })
                }}
                onClick={() => {
                  if (isTabletUp) {
                    handleClick();
                  }
                  setExpanded(!expanded);
                }}
                {...whatYouGet?.cta}
                endIcon={
                  <DtvAvatar
                    alt=""
                    src="https://www.directv.com/dtvassets/global/icons/fusion/arrow_jet_up.svg"
                    sx={{
                      height: "16px",
                      width: "16px",
                      ...(!expanded && { transform: "rotate(180deg)" })
                    }}
                  />
                }
              />
            )}
            {isComparePkgTray && <DtvBox
              sx={{
                '& div label': {
                  alignItems: "center !important"
                },
                '& div label span:last-child': {
                  fontSize: "14px !important"
                },
                '& .MuiCheckbox-root': {
                  width: "24px !important",
                  height: "24px !important",
                  [DtvTheme.breakpoints.between(DtvTheme.breakpoints.values.sm, DtvTheme.breakpoints.values.md)]: {
                    marginRight: "6px"
                  }
                }
              }}
            ><Checkbox
              checked={selectedItems.includes(queryparam)}
              {...{
              mode: compareText?.theme || "light",
              variant: "Checkbox Label",
              checkboxoptions: [
                {
                label: compareText?.content,
                value: "compare",
                checked: false,
                disabled: false,
                // Fusion's CheckBox puts these on the <input> as aria-label /
                // data-linkposition / data-linkdestination (aria-label falls back to the label).
                ...(pageUrl && compareText?.content && {
                  linkPosition: "genre_card",
                  linkDestination: modalLinkDestination(pageUrl, "comparison_tray")
                })
                }
              ]
              }}
              onChange={event => {
                event?.stopPropagation();
                handleCompareEvent(queryparam);
              }}
            /></DtvBox>}
          </DtvBox>
        </EqualHeightElement>

        <Collapse in={expanded} timeout="auto" >
          <EqualHeightElement
            disable={disableEqualHeight}
            name="plan-card-feature"
          >
            {whatYouGet?.heading?.content && (
              <>
                <DtvBox
                  sx={{
                    margin: "8px 0 0 0"
                  }}
                >
                  <DtvText
                    component="span"
                    id={`${id}-whatYouGet-heading`}
                    variant="body2"
                    {...whatYouGet?.heading}
                  />
                  &nbsp;
                  <DtvText
                    component="span"
                    id={`${id}-whatYouGet-basePackageFeature`}
                    variant="body2medium"
                    {...whatYouGet?.basePackageFeature}
                  />
                </DtvBox>
                <DtvBox
                  sx={{
                    margin: "8px 0 0 0",
                    display: "flex",
                    justifyContent: "center"
                  }}
                >
                  <DtvText color={noTopBorder ? "#3885E1" : ""} content="+" variant="subhead2medium"/>
                </DtvBox>
              </>
            )}
            <PackageFeatureList
              features={features}
              id={`${id}-pkg-feature-list`}
              noTopBorder={noTopBorder}
              packageCardVariant={packageCardVariant}
              showNoLocalChannels={showNoLocalChannels}
              textColor={textColor}
            />
            {comparePackagesCta?.label && (
              <DtvButton
                ariaLabel={comparePackagesCta?.label}
                inline={true}
                typographyVariant="body3"
                variant="uxlink"
                sx={{
                  color: "#2E70BD",
                  "&:focus": {
                    outline: "2px solid #2E70BD",
                    padding: "0 5px"
                  }
                }}
                onClick={openComparePackagesModal}
                {...comparePackagesCta}
                analytics={null}
                googleAnalyticsData={getAnalyticsAttrs(comparePackagesCta?.label, "panel_plan card", "/modal/compare packages")}
              />
            )}
          </EqualHeightElement>
        </Collapse>
      </>
    );
  } catch (error) {
    return <ErrorDisplay error={error} />;
  }
};
